function isFunction(functionToCheck) {
  return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
}

export default {
  name: 'FormFieldMixin',
  props: {
    name: {
      type: String,
      required: true,
    },
    className: {
      type: String,
      default: '',
    },
    labelClass: {
      type: String,
      default: '',
    },
    // fieldWrapperClass: {
    //   type: String,
    //   default: undefined,
    // },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    labelSlot: {
      type: [Function],
      default: null,
    },
    value: {
      type: [String, Array, Number, Boolean, Date, Object],
      default: '',
    },
    validation: {
      type: [String, Object],
    },
    isShowStar: {
      type: Boolean,
      default: false,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    asyncValidation: {
      type: Function,
    },
    disabled: {
      type: [Function, Boolean],
      default: false,
    },
    display: {
      type: [Function, Boolean],
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    horizontalLayout: {
      type: Boolean,
      default: false,
    },
    warningMessage: {
      type: String,
      default: '',
    },
    notice: {
      type: [Function, String],
      default: '',
    },
    variant: {
      type: String,
      default: 'normal',
      validator: value => ['normal', 'fill'].includes(value),
    },
  },
  computed: {
    errorMsg() {
      return this.errors.first(this.name);
    },
    isInvalid() {
      return this.errors.has(this.name);
    },
    isDisplay() {
      if (isFunction(this.display)) {
        return this.display.bind(this)();
      }
      return this.display;
    },
  },
  data() {
    return {
      isDirty: false,
      isChanged: false,
      initialValue: '',
      localValue: '',
      forceInvalidated: false
    };
  },
  mounted() {
    this.initialValue = this.value || '';
    this.localValue = this.value || '';
  },
  methods: {
    validateAsync(formData) {
      return new Promise((resolve) => {
        this.asyncValidation(this.localValue, resolve, formData);
      });
    },
    forceInvalidate() {
       this.forceInvalidated = true
    },
    forceValidate() {
      this.forceInvalidated = false
    },
    setValue(value) {
      this.localValue = value;
    },
    setPristine() {
      this.isDirty = false;
      this.isChanged = false;
      this.initialValue = this.localValue;
    },
    reset() {
      this.localValue = this.initialValue;
      this.setPristine();
      this.$emit('input', this.localValue);
    },
    renderLabel(h) {
      if (this.hideLabel) {
        return null;
      }
      const childrenComponents = [];
      const labelClass = this.labelClass ? { [this.labelClass]: true } : undefined;

      if (this.labelSlot) {
        childrenComponents.push(this.labelSlot(h));
        return h('div', { class: { 'label-wrapper': true } }, [
          h('label', {
            class: labelClass,
            attrs: { for: this.name },
            domProps: { innerText: this.$t(this.label) },
          }),
          ...childrenComponents,
        ]);
      }

      if (this.validation === 'required' || (this.validation && this.validation.required) || this.isShowStar) {
        childrenComponents.push(
          h('span', {
            attrs: { style: 'color: red;' },
            domProps: { innerText: '  *' },
          }),
        );
      }

      return h('div', { class: { 'label-wrapper': true } }, [
        h('label', {
          class: labelClass,
          attrs: { for: this.name },
          domProps: { innerText: this.$t(this.label) },
        }),
        ...childrenComponents,
      ]);
    },
    renderErrorText(h) {
      return h('span', {
        class: {
          'invalid-feedback': true,
          'is-invalid': this.isInvalid,
        },
        domProps: { innerText: this.errorMsg },
        directives: [
          { name: 'show', rawName: 'v-show', value: this.isInvalid, expression: 'isInvalid' },
        ],
      });
    },
    renderWarningLabel(h) {
      return h('div', {
        class: {
          'field-warning': true,
        },
        directives: [
          { name: 'show', rawName: 'v-show', value: !!this.warningMessage, expression: '!!warningMessage' },
        ],
      }, [
        h('i', { class: { 'fa fa-exclamation-triangle mr-2': true } }),
        h('span', { domProps: { innerText: this.warningMessage } }),
      ]);
    },
    renderNotice(h) {
      return h('div', {
        class: {
          'field-notice': true,
        },
        directives: [
          { name: 'show', rawName: 'v-show', value: !!this.notice, expression: '!!notice' },
        ],
      }, [
        h('span', { domProps: { innerHTML: this.notice } }),
      ]);
    },
    renderShowPasswordIcon(h) {
      return h('i', {
        class: {
          'far fa-eye': true,
          'is-invalid': this.isInvalid,
        },
        on: {
          click: (e) => {
            const isHiddenText = e.target.classList.value.indexOf('fa-eye') > 0;
            const disclosureText = e.target.classList.value.indexOf('fa-eye-slash') > 0;
            if (isHiddenText) {
              e.target.classList.remove('fa-eye');
              e.target.classList.add('fa-eye-slash');
              e.target.parentElement.querySelector('input').setAttribute('type', 'text');
            }
            if (disclosureText) {
              e.target.classList.remove('fa-eye-slash');
              e.target.classList.add('fa-eye');
              e.target.parentElement.querySelector('input').setAttribute('type', 'password');
            }
          },
        },
      });
    },
    renderFormGroup(h) {
      let childrenComponents = [
        this.renderLabel(h),
        this.renderField(h),
        (this.validation || this.asyncValidation) ? this.renderErrorText(h) : null,
        this.warningMessage ? this.renderWarningLabel(h) : null,
        this.notice ? this.renderNotice(h) : null,
      ];
      // const fieldWrapperClass = this.fieldWrapperClass ? { [this.fieldWrapperClass]: true } : {};
      // Object.assign({ 'field-wrapper': true }, fieldWrapperClass)
      if (this.horizontalLayout) {
        childrenComponents = [
          h(
            'div', { class: { 'd-flex horizontal': true } },
            [
              this.renderLabel(h),
              h(
                'div', { class: { 'field-wrapper': true } },
                [
                  this.renderField(h),
                  (this.validation || this.asyncValidation) ? this.renderErrorText(h) : null,
                  this.warningMessage ? this.renderWarningLabel(h) : null,
                  this.notice ? this.renderNotice(h) : null,
                ],
              ),
            ],
          ),
        ];
      }

      if (this.renderSlot) {
        this.renderSlot.forEach((vnode) => {
          childrenComponents.push(vnode);
        });
      }
      if (this.inputType === 'password') {
        childrenComponents.push(this.renderShowPasswordIcon(h));
      }

      if (this.inputType === 'file' && this.draggable) {
        childrenComponents = [
          this.renderDragAndDrop(h),
        ];
        if (this.localValue && !!this.localValue.length) {
          childrenComponents.push(this.renderListFile(h));
        } else {
          childrenComponents.push(this.renderNotice(h));
        }
      }

      if (this.datalist && !!this.options.length) {
        childrenComponents.push(this.renderDataList(h));
      }

      const classes = { 'form-group': true, 'input-password': this.inputType === 'password' };
      if (this.className) {
        classes[this.className] = true;
      }
      return h(
        'transition',
        { props: { name: 'slide-fade', mode: 'out-in' } },
        [h(
          'div',
          { class: classes },
          childrenComponents,
        )],
      );
    },
    renderDragAndDrop(h) {
      const dropZonePrompt = h('span', {
        class: {
          'drop-zone-prompt': true,
        },
        // domProps: { innerText: `${!!this.localValue.length} files` },
        on: {
          click: (evt) => {
            evt.currentTarget.parentElement
              .querySelector('input.drag-and-drop')
              .dispatchEvent(new MouseEvent('click',
                { bubbles: true, cancelable: true }));
          },
        },
      },
        this.action,
      );

      return h(
        'div', {
          class: 'drag-container',
        },
        [
          h('div', {
          }, [
            h('img', { domProps: { src: '/images/upload-icon.svg' } }, []),
            dropZonePrompt,
          ]),
          this.renderField(h),
        ],
      );
    },
    renderListFile(h) {
      const items = [];
      for (let i = 0; i < this.localValue.length; i++) {
        items.push(h(
          {
            props: ['nameData', 'indexData'],
            template: `
              <div class="uploaded-list-item">
                <i class="fa fa-paperclip"></i>
                <span>{{ nameData }}</span>
                <i class="fa fa-trash" @click="updateChildData(indexData)"></i>
              </div>
            `,
            methods: {
              updateChildData(index) {
                this.$bus.emit('remove-file-item', index);
              },
            },
          },
          {
            props: {
              nameData: this.localValue[i].name,
              indexData: i,
            },
          },
        ));
      }
      return h(
        'div', {
          class: 'uploaded-list-item-container',
        },
        items,
      );
    },
    renderDataList(h) {
      const items = this.options.map(option => h('option', {
        attrs: {
          value: option,
        },
      }));
      return h(
        'datalist', {
          attrs: {
            id: this.datalist,
          },
          class: 'data-list-container',
        },
        items,
      );
    },
  },
  watch: {
    isChanged(newVal) {
      this.$emit('input-modified', {
        isChanged: newVal,
        field: this.name,
        value: {
          oldVal: this.initialValue,
          newVal: this.localValue,
        },
      });
    },
    isDirty(newVal) {
      this.$emit('input-dirty-changed', {
        isDirty: newVal,
        field: this.name,
      });
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/app-form/form-field-mixin.js